import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Upload, X, FileText, Image, Loader2, ChevronDown, ChevronUp, User, Briefcase, Phone } from 'lucide-react';
import { getSession, saveCategory, uploadEvidence, deleteEvidence, getRubricData, completeSurvey, updateUserInfo } from '../utils/api';

const CATEGORIES = [
  'asset_readiness',
  'maintenance_readiness', 
  'capital_planning_readiness',
  'compliance_risk_readiness',
  'data_governance_readiness'
];

function SurveyPage() {
  const navigate = useNavigate();
  const { sessionId, categoryId } = useParams();
  const [session, setSession] = useState(null);
  const [rubricData, setRubricData] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [scores, setScores] = useState({ a: null, b: null, c: null, d: null, e: null });
  const [notes, setNotes] = useState({ a: '', b: '', c: '', d: '', e: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [completionStatus, setCompletionStatus] = useState(null); // Track completion progress
  // All questions expanded by default - track which ones are collapsed
  const [collapsedQuestions, setCollapsedQuestions] = useState({});
  // Store session in a ref so we can access latest value without triggering re-renders
  const sessionRef = useRef(null);
  // Completion form state
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [completionData, setCompletionData] = useState({
    contact_name: '',
    job_title: '',
    phone: ''
  });

  const currentCategory = CATEGORIES[currentCategoryIndex];
  const categoryData = rubricData?.categories?.find(c => c.id === currentCategory);

  // Load session and rubric data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [sessionData, rubric] = await Promise.all([
          getSession(sessionId),
          getRubricData()
        ]);
        setSession(sessionData);
        sessionRef.current = sessionData;
        setRubricData(rubric);
        
        // If categoryId is provided, find its index
        if (categoryId) {
          const idx = CATEGORIES.indexOf(categoryId);
          if (idx >= 0) setCurrentCategoryIndex(idx);
        }
        
        // Load initial scores for first category
        const initialCategory = categoryId ? CATEGORIES[CATEGORIES.indexOf(categoryId)] : CATEGORIES[0];
        const catData = sessionData.rubric_scores?.[initialCategory] || {};
        setScores({
          a: catData.a || null,
          b: catData.b || null,
          c: catData.c || null,
          d: catData.d || null,
          e: catData.e || null
        });
        setNotes(catData.notes || { a: '', b: '', c: '', d: '', e: '' });
        
        // Scroll to top when survey loads so Q1 is visible
        window.scrollTo({ top: 0, behavior: 'instant' });
      } catch (error) {
        console.error('Failed to load data:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [sessionId, categoryId, navigate]);

  // Load scores when category changes (NOT when session updates)
  useEffect(() => {
    // Skip on initial render (session not loaded yet)
    if (!sessionRef.current) return;
    
    const catData = sessionRef.current.rubric_scores?.[currentCategory] || {};
    setScores({
      a: catData.a || null,
      b: catData.b || null,
      c: catData.c || null,
      d: catData.d || null,
      e: catData.e || null
    });
    setNotes(catData.notes || { a: '', b: '', c: '', d: '', e: '' });
    // Reset collapsed state when changing categories
    setCollapsedQuestions({});
    
    // Scroll to top when category changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentCategoryIndex]); // Only depends on category index, NOT session

  // Calculate suggested score to 2 decimal places
  const calculateSuggestedScore = useCallback(() => {
    const values = Object.values(scores).filter(v => v !== null);
    if (values.length === 0) return null;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    return Math.round(avg * 100) / 100; // Round to 2 decimal places
  }, [scores]);

  const suggestedScore = calculateSuggestedScore();

  // Check if a category is complete (from saved session data)
  const isCategoryCompleteSaved = (cat) => {
    const catData = session?.rubric_scores?.[cat];
    if (!catData) return false;
    return ['a', 'b', 'c', 'd', 'e'].every(q => catData[q] !== null);
  };

  // Check if current category is complete based on local (unsaved) scores
  const isCurrentCategoryComplete = () => {
    return ['a', 'b', 'c', 'd', 'e'].every(q => scores[q] !== null);
  };

  // Check if all categories are complete - considers unsaved local state for current category
  const allCategoriesComplete = CATEGORIES.every((cat, idx) => {
    if (idx === currentCategoryIndex) {
      // For current category, check local scores (may not be saved yet)
      return isCurrentCategoryComplete();
    }
    // For other categories, check saved session data
    return isCategoryCompleteSaved(cat);
  });

  const handleSaveCategory = async () => {
    setSaving(true);
    try {
      const data = {
        ...scores,
        notes_a: notes.a,
        notes_b: notes.b,
        notes_c: notes.c,
        notes_d: notes.d,
        notes_e: notes.e
        // No final_score override - system calculates it
      };
      const updatedSession = await saveCategory(sessionId, currentCategory, data);
      sessionRef.current = updatedSession; // Update ref so next category loads correct data
      setSession(updatedSession);
      return true;
    } catch (error) {
      console.error('Failed to save:', error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const saved = await handleSaveCategory();
    if (saved) {
      if (currentCategoryIndex < CATEGORIES.length - 1) {
        setCurrentCategoryIndex(currentCategoryIndex + 1);
        window.scrollTo(0, 0);
      }
    }
  };

  const handlePrevious = async () => {
    await handleSaveCategory();
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(currentCategoryIndex - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleComplete = async () => {
    // Show completion form first to collect additional info
    setShowCompletionForm(true);
  };

  const handleFinalComplete = async () => {
    setSaving(true);
    setCompletionStatus('saving');
    try {
      // Save current category first
      const data = {
        ...scores,
        notes_a: notes.a,
        notes_b: notes.b,
        notes_c: notes.c,
        notes_d: notes.d,
        notes_e: notes.e
      };
      await saveCategory(sessionId, currentCategory, data);
      
      // Update user info with completion form data
      if (completionData.contact_name || completionData.job_title || completionData.phone) {
        await updateUserInfo(sessionId, completionData);
      }
      
      // Update status - now analyzing
      setCompletionStatus('analyzing');
      
      // Then complete the survey (includes AI analysis)
      await completeSurvey(sessionId);
      
      // Navigate to results
      navigate(`/results/${sessionId}`);
    } catch (error) {
      console.error('Failed to complete survey:', error);
      // Show more specific error message if available
      const errorMessage = error.response?.data?.detail || 'Failed to complete survey. Please ensure all categories are filled.';
      alert(errorMessage);
      setCompletionStatus(null);
      setShowCompletionForm(false); // Close modal so user can check their answers
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (question, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(question);
    try {
      await uploadEvidence(sessionId, currentCategory, question, file);
      const updatedSession = await getSession(sessionId);
      // Update session state for evidence display, but don't trigger score sync
      sessionRef.current = updatedSession;
      setSession(updatedSession);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file.');
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteEvidence = async (evidenceId) => {
    try {
      await deleteEvidence(sessionId, evidenceId);
      const updatedSession = await getSession(sessionId);
      // Update session state for evidence display, but don't trigger score sync
      sessionRef.current = updatedSession;
      setSession(updatedSession);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const getEvidenceForQuestion = (question) => {
    return session?.evidence?.filter(
      e => e.category === currentCategory && e.question === question
    ) || [];
  };

  const getEvidenceStrength = (cat) => {
    return session?.category_evidence_strength?.[cat] || 0;
  };

  const toggleQuestion = (qId) => {
    setCollapsedQuestions(prev => ({
      ...prev,
      [qId]: !prev[qId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cloud-grey">
        <Loader2 className="w-8 h-8 animate-spin text-primary-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cloud-grey">
      {/* Header */}
      <header className="bg-deep-navy border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="text-slate-grey hover:text-white flex items-center gap-2 font-inter"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Exit
            </button>
            <div className="text-sm text-slate-grey font-lato" data-testid="progress-text">
              Category {currentCategoryIndex + 1} of {CATEGORIES.length}
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex gap-2">
            {CATEGORIES.map((cat, idx) => {
              // For current category, check local scores; for others, check saved session
              const isComplete = idx === currentCategoryIndex 
                ? isCurrentCategoryComplete() 
                : isCategoryCompleteSaved(cat);
              const isCurrent = idx === currentCategoryIndex;
              const catInfo = rubricData?.categories?.find(c => c.id === cat);
              
              // Short names for mobile
              const shortNames = {
                'asset_readiness': 'Assets',
                'maintenance_readiness': 'Maint',
                'capital_planning_readiness': 'Capital',
                'compliance_risk_readiness': 'Risk',
                'data_governance_readiness': 'Data'
              };
              
              return (
                <button
                  key={cat}
                  onClick={async () => {
                    await handleSaveCategory();
                    setCurrentCategoryIndex(idx);
                  }}
                  className={`flex-1 py-2 px-1 sm:px-3 rounded-lg text-xs font-inter font-medium transition-all ${
                    isCurrent 
                      ? 'bg-primary-blue text-white' 
                      : isComplete 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`progress-${cat}`}
                >
                  <div className="flex items-center justify-center gap-1">
                    {isComplete && !isCurrent && <Check className="w-3 h-3" />}
                    <span className="hidden lg:inline">{catInfo?.name || cat}</span>
                    <span className="hidden sm:inline lg:hidden">{shortNames[cat] || cat}</span>
                    <span className="sm:hidden">{shortNames[cat] || (idx + 1)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Category Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-inter font-bold text-deep-navy mb-2" data-testid="category-title">
            {categoryData?.name}
          </h1>
          <p className="text-slate-grey font-lato" data-testid="category-description">
            {categoryData?.description}
          </p>
        </div>

        {/* Questions - All expanded by default */}
        <div className="space-y-6">
          {categoryData?.questions?.map((question, qIdx) => {
            const qId = question.id;
            const evidence = getEvidenceForQuestion(qId);
            // Questions are expanded unless explicitly collapsed
            const isExpanded = !collapsedQuestions[qId];

            return (
              <div
                key={qId}
                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                data-testid={`question-${qId}`}
              >
                {/* Question Header */}
                <div 
                  className="p-4 sm:p-6 cursor-pointer hover:bg-cloud-grey"
                  onClick={() => toggleQuestion(qId)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="w-8 h-8 bg-primary-100 text-primary-blue rounded-full flex items-center justify-center font-inter font-semibold text-sm">
                          {qId.toUpperCase()}
                        </span>
                        <h3 className="font-inter font-semibold text-deep-navy">{question.title}</h3>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {scores[qId] && (
                        <span className="px-3 py-1 bg-primary-100 text-primary-blue rounded-full text-sm font-inter font-medium">
                          Score: {scores[qId]}
                        </span>
                      )}
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-grey" /> : <ChevronDown className="w-5 h-5 text-slate-grey" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Content - shown by default */}
                {isExpanded && (
                  <div className="border-t border-slate-100 p-4 sm:p-6 bg-cloud-grey">
                    {/* Rubric */}
                    <div className="mb-6">
                      <h4 className="text-sm font-inter font-medium text-deep-navy mb-3">Scoring Rubric</h4>
                      <div className="space-y-2">
                        {[5, 4, 3, 2, 1].map(score => (
                          <div
                            key={score}
                            className={`rubric-item p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              scores[qId] === score
                                ? 'border-primary-blue bg-primary-50'
                                : 'border-transparent bg-white hover:border-slate-200'
                            }`}
                            onClick={() => setScores({ ...scores, [qId]: score })}
                            data-testid={`rubric-${qId}-${score}`}
                          >
                            <div className="flex items-start gap-3">
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center font-inter font-bold text-sm ${
                                scores[qId] === score
                                  ? 'bg-primary-blue text-white'
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {score}
                              </span>
                              <p className="text-sm text-slate-700 font-lato flex-1 pt-1">
                                {question.rubric[score]}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Explain Your Answer */}
                    <div className="mb-6">
                      <label className="text-sm font-inter font-medium text-deep-navy mb-2 block">Explain your answer (optional)</label>
                      <textarea
                        value={notes[qId] || ''}
                        onChange={(e) => setNotes({ ...notes, [qId]: e.target.value })}
                        placeholder="Describe your current processes, systems, or examples that support your score..."
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent resize-none font-lato"
                        rows={3}
                        data-testid={`notes-${qId}`}
                      />
                      <p className="text-xs text-slate-grey font-lato mt-1">
                        Detailed explanations help our AI provide more accurate recommendations
                      </p>
                    </div>

                    {/* Evidence Upload - HIDDEN (kept for future use) */}
                    {false && (
                    <div>
                      <label className="text-sm font-inter font-medium text-deep-navy mb-2 block">Evidence (optional)</label>
                      <p className="text-sm text-slate-grey font-lato mb-3">{question.evidence_prompt}</p>
                      
                      {/* Uploaded Files */}
                      {evidence.length > 0 && (
                        <div className="mb-3 space-y-2">
                          {evidence.map(ev => (
                            <div key={ev.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                              <div className="flex items-center gap-2">
                                {ev.mime_type?.startsWith('image') ? (
                                  <Image className="w-4 h-4 text-primary-blue" />
                                ) : (
                                  <FileText className="w-4 h-4 text-action-amber" />
                                )}
                                <span className="text-sm text-deep-navy font-lato truncate max-w-[200px]">{ev.filename}</span>
                              </div>
                              <button
                                onClick={() => handleDeleteEvidence(ev.id)}
                                className="p-1 hover:bg-red-100 rounded text-red-500"
                                data-testid={`delete-evidence-${ev.id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Upload Button */}
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg cursor-pointer hover:bg-cloud-grey transition-all font-inter text-sm">
                        {uploading === qId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        <span>Upload File</span>
                        <input
                          type="file"
                          className="hidden"
                          accept=".jpg,.jpeg,.png,.pdf,.csv,.xlsx,.heic"
                          onChange={(e) => handleFileUpload(qId, e)}
                          disabled={uploading === qId}
                          data-testid={`upload-${qId}`}
                        />
                      </label>
                    </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Score Summary Card - No override option */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-inter font-semibold text-deep-navy mb-1">Category Score</h3>
              <p className="text-sm text-slate-grey font-lato">
                Evidence Strength: {getEvidenceStrength(currentCategory)}/5 
                ({getEvidenceStrength(currentCategory) <= 1 ? 'Weak' : getEvidenceStrength(currentCategory) <= 3 ? 'Moderate' : 'Strong'})
              </p>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-grey font-lato mb-1">Category Score</div>
              <div className="text-3xl font-inter font-bold text-primary-blue" data-testid="category-score">
                {suggestedScore !== null ? suggestedScore.toFixed(2) : '-'}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentCategoryIndex === 0 || saving}
            className="px-6 py-3 flex items-center gap-2 text-slate-grey hover:text-deep-navy font-inter disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>
          
          <div className="flex gap-3">
            {currentCategoryIndex < CATEGORIES.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={saving}
                className="px-6 py-3 bg-primary-blue hover:bg-primary-600 text-white font-inter font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
                data-testid="next-btn"
              >
                {saving ? 'Saving...' : 'Save & Continue'}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={saving || !allCategoriesComplete}
                className="px-6 py-3 bg-action-amber hover:bg-accent-600 text-deep-navy font-inter font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
                data-testid="complete-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {completionStatus === 'analyzing' ? 'Analyzing...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    Complete Survey
                    <Check className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* AI Analysis Progress Indicator */}
        {saving && completionStatus && (
          <div className="mt-6 mx-auto max-w-md">
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-violet-800">
                    {completionStatus === 'saving' ? 'Saving your responses...' : 'AI Analysis in Progress'}
                  </h3>
                  <p className="text-sm text-violet-600">
                    {completionStatus === 'saving' 
                      ? 'Please wait while we save your final answers.'
                      : 'Our AI agents are analyzing your assessment data, notes, and evidence to generate personalized insights.'}
                  </p>
                </div>
              </div>
              
              {completionStatus === 'analyzing' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-violet-700">
                    <Check className="w-3 h-3 text-green-500" />
                    <span>Scores calculated</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-violet-700">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Reviewing notes and evidence...</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-violet-400">
                    <div className="w-3 h-3 rounded-full border border-violet-300" />
                    <span>Generating recommendations with cost estimates...</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-violet-400">
                    <div className="w-3 h-3 rounded-full border border-violet-300" />
                    <span>Preparing your personalized report...</span>
                  </div>
                </div>
              )}
              
              <p className="mt-3 text-xs text-violet-500 text-center italic">
                This typically takes 15-30 seconds
              </p>
            </div>
          </div>
        )}

        {/* Completion Hint */}
        {!allCategoriesComplete && currentCategoryIndex === CATEGORIES.length - 1 && (
          <p className="mt-4 text-center text-sm text-amber-600">
            Complete all 5 categories to generate your results report.
          </p>
        )}
      </main>

      {/* Completion Form Modal */}
      {showCompletionForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-action-amber to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-inter font-bold text-deep-navy mb-2">Almost Done!</h2>
              <p className="text-slate-grey font-lato">Help us personalize your report by telling us a bit about yourself.</p>
            </div>
            
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-inter font-medium text-deep-navy mb-1">
                  Your Name <span className="text-action-amber">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-grey" />
                  <input
                    type="text"
                    value={completionData.contact_name}
                    onChange={(e) => setCompletionData({...completionData, contact_name: e.target.value})}
                    placeholder="John Smith"
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-lato"
                    data-testid="completion-name-input"
                  />
                </div>
              </div>
              
              {/* Job Title */}
              <div>
                <label className="block text-sm font-inter font-medium text-deep-navy mb-1">
                  Job Title <span className="text-action-amber">*</span>
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-grey" />
                  <input
                    type="text"
                    value={completionData.job_title}
                    onChange={(e) => setCompletionData({...completionData, job_title: e.target.value})}
                    placeholder="Facilities Manager"
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-lato"
                    data-testid="completion-title-input"
                  />
                </div>
              </div>
              
              {/* Mobile Phone - Optional */}
              <div>
                <label className="block text-sm font-inter font-medium text-deep-navy mb-1">
                  Mobile Phone <span className="text-slate-400 text-xs font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-grey" />
                  <input
                    type="tel"
                    value={completionData.phone}
                    onChange={(e) => setCompletionData({...completionData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-lato"
                    data-testid="completion-phone-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowCompletionForm(false)}
                className="flex-1 py-3 border border-slate-300 text-slate-600 font-inter font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleFinalComplete}
                disabled={!completionData.contact_name || !completionData.job_title || saving}
                className="flex-1 py-3 bg-action-amber hover:bg-amber-500 text-deep-navy font-inter font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="final-complete-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {completionStatus === 'analyzing' ? 'Analyzing...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    Get My Results
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
            
            <p className="mt-4 text-xs text-center text-slate-grey font-lato">
              Your information helps us provide more relevant recommendations.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default SurveyPage;
